import React, { useState } from 'react';

const COLORS = {
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  amber: '#d29922',
  red: '#f85149',
};

const scoreColor = (s) => s > 0.02 ? COLORS.green : s < -0.02 ? COLORS.red : COLORS.textMuted;

function MiniBar({ value }) {
  const norm = (value + 1) / 2; // -1..1 → 0..1
  const chars = 8;
  const filled = Math.round(norm * chars);
  return (
    <span style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 1, color: scoreColor(value) }}>
      {'▓'.repeat(filled)}
      <span style={{ color: COLORS.border }}>{'░'.repeat(chars - filled)}</span>
    </span>
  );
}

function TierBadge({ tier }) {
  const isT1 = tier === 1;
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      borderRadius: 8,
      fontSize: 9,
      fontWeight: 700,
      marginLeft: 6,
      background: isT1 ? '#c9922022' : '#8b949e22',
      color: isT1 ? '#d4a017' : '#8b949e',
      border: `1px solid ${isT1 ? '#d4a01766' : '#8b949e44'}`,
    }}>
      T{tier}
    </span>
  );
}

export default function SourceBreakdown({ source_breakdown, tier_comparison }) {
  const [sortKey, setSortKey] = useState('composite_avg');
  const [sortAsc, setSortAsc] = useState(false);

  if (!source_breakdown) return null;

  const sorted = [...source_breakdown].sort((a, b) => {
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    return sortAsc ? av - bv : bv - av;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const cols = [
    { key: 'source', label: 'Source' },
    { key: 'count', label: 'Articles' },
    { key: 'oil_avg', label: 'Oil' },
    { key: 'fed_avg', label: 'Fed' },
    { key: 'geo_avg', label: 'Geo' },
    { key: 'consumer_avg', label: 'Consumer' },
    { key: 'earnings_avg', label: 'Earnings' },
    { key: 'composite_avg', label: 'Composite' },
  ];

  const t1 = tier_comparison?.tier1;
  const t2 = tier_comparison?.tier2;
  const divergence = t1 && t2 ? Math.abs(t1.composite_avg - t2.composite_avg) : 0;
  const isDiverging = divergence > 0.15;

  const indicators = ['oil_avg', 'fed_avg', 'geo_avg', 'consumer_avg', 'earnings_avg'];
  const indLabels = { oil_avg: 'Oil', fed_avg: 'Fed', geo_avg: 'Geo', consumer_avg: 'Consumer', earnings_avg: 'Earnings' };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
        Source Breakdown
      </div>

      {/* Tier legend */}
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        fontSize: 12,
        color: COLORS.textMuted,
      }}>
        <div>
          <span style={{ color: '#d4a017', fontWeight: 700 }}>T1 — Tier 1</span>
          {' '}Premium financial press (WSJ, Bloomberg, FT, Investing.com, BBC). Authoritative but mostly paywalled — scored from headlines only.
        </div>
        <div>
          <span style={{ color: COLORS.textMuted, fontWeight: 700 }}>T2 — Tier 2</span>
          {' '}Mainstream &amp; community sources (CNBC, WaPo, NPR, Reddit, StockTwits, MarketWatch). Higher volume, full article text scored.
        </div>
      </div>

      {/* Sortable table */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'monospace' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                {cols.map(c => (
                  <th key={c.key}
                    onClick={() => handleSort(c.key)}
                    style={{
                      padding: '8px 10px', textAlign: 'left', color: COLORS.textMuted,
                      fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
                      cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                    }}>
                    {c.label} {sortKey === c.key ? (sortAsc ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: '6px 10px', color: COLORS.text }}>
                    {r.source}<TierBadge tier={r.source_tier} />
                  </td>
                  <td style={{ padding: '6px 10px', color: COLORS.textMuted }}>{r.count}</td>
                  {['oil_avg', 'fed_avg', 'geo_avg', 'consumer_avg', 'earnings_avg'].map(k => (
                    <td key={k} style={{ padding: '6px 10px', color: scoreColor(r[k]) }}>
                      {r[k]?.toFixed(3)}
                    </td>
                  ))}
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ color: scoreColor(r.composite_avg), fontWeight: 700, marginRight: 6 }}>
                      {r.composite_avg?.toFixed(3)}
                    </span>
                    <MiniBar value={r.composite_avg || 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tier divergence card */}
      {t1 && t2 && (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>Tier Divergence</div>
            <span style={{
              padding: '4px 12px',
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 700,
              background: isDiverging ? `${COLORS.amber}22` : `${COLORS.green}22`,
              color: isDiverging ? COLORS.amber : COLORS.green,
              border: `1px solid ${isDiverging ? COLORS.amber : COLORS.green}`,
            }}>
              {isDiverging ? '⚠️ DIVERGING' : '✓ ALIGNED'} ({divergence.toFixed(3)})
            </span>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16 }}>
            When T1 and T2 diverge significantly (&gt;0.15), it may indicate a split between institutional press and retail/community sentiment.
          </div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#d4a017', marginBottom: 4 }}>TIER 1 ({t1.count})</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(t1.composite_avg), fontFamily: 'monospace' }}>
                {t1.composite_avg?.toFixed(3)}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4 }}>TIER 2 ({t2.count})</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(t2.composite_avg), fontFamily: 'monospace' }}>
                {t2.composite_avg?.toFixed(3)}
              </div>
            </div>
          </div>

          {/* Per-indicator comparison */}
          {indicators.map(k => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '3px 0', fontSize: 11, fontFamily: 'monospace' }}>
              <span style={{ width: 70, color: COLORS.textMuted }}>{indLabels[k]}</span>
              <span style={{ width: 60, color: scoreColor(t1[k]) }}>{t1[k]?.toFixed(3)}</span>
              <span style={{ width: 30, textAlign: 'center', color: COLORS.textMuted }}>vs</span>
              <span style={{ width: 60, color: scoreColor(t2[k]) }}>{t2[k]?.toFixed(3)}</span>
              <span style={{ marginLeft: 8, color: Math.abs(t1[k] - t2[k]) > 0.15 ? COLORS.amber : COLORS.textMuted }}>
                Δ{Math.abs(t1[k] - t2[k]).toFixed(3)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
